import {
  IAddress,
  IBasePagingRes,
  ICreateMicroDevice,
  ICsqHistory,
  IDevice,
  IDeviceBody,
  IDeviceError,
  IDeviceErrorQuery,
  IDeviceHistory,
  IDeviceHistoryCsqQuery,
  IDeviceHistoryInstallQuery,
  IDeviceHistoryQuery,
  IDeviceQuery,
  IImportDeviceBody,
  ILicenseDeviceBody,
  ILocationDeviceBody,
  IShare,
  IShareDeviceBody,
  IShareDeviceMultiBody,
  IVerifyDeviceBody,
  IVolumeBody,
} from 'src/types';
import { http } from 'src/utils';
import BaseService from './base.service';

export default class DeviceService extends BaseService<IDevice, IDeviceQuery, IDeviceBody> {
  constructor() {
    super('/api/device');
  }

  public async createMicro(body: ICreateMicroDevice) {
    const { data } = await http.post<boolean>(`${this.url}/micro`, body);
    return data;
  }

  public async updateMicro(id: string, body: ICreateMicroDevice) {
    const { data } = await http.put<boolean>(`${this.url}/micro/${id}`, body);
    return data;
  }

  public async verify(body: IVerifyDeviceBody) {
    const { data } = await http.post<any>(`${this.url}/verify`, body);
    return data;
  }

  public async history(id: string, params: IDeviceHistoryQuery) {
    const { data } = await http.get<IBasePagingRes<IDeviceHistory>>(`${this.url}/history/${id}`, { params });
    return data;
  }

  public async historyInstall(id: string, params: IDeviceHistoryInstallQuery) {
    const { data } = await http.get<IBasePagingRes<IAddress>>(`${this.url}/history-install/${id}`, { params });
    return data;
  }

  public async historyCsq(id: string, params: IDeviceHistoryCsqQuery) {
    const { data } = await http.get<IBasePagingRes<ICsqHistory>>(`${this.url}/history-csq/${id}`, { params });
    return data;
  }

  public async historyError(id: string, params: IDeviceErrorQuery) {
    const { data } = await http.get<IBasePagingRes<IDeviceError>>(`${this.url}/error/${id}`, { params });
    return data;
  }

  public async volume(body: IVolumeBody) {
    const { data } = await http.put<boolean>(`${this.url}/volume`, body);
    return data;
  }

  public async pause(id: string) {
    const { data } = await http.put<boolean>(`${this.url}/pause/${id}`);
    return data;
  }

  public async play(id: string) {
    const { data } = await http.put<boolean>(`${this.url}/play/${id}`);
    return data;
  }

  public async stop(id: string) {
    const { data } = await http.put<boolean>(`${this.url}/stop/${id}`);
    return data;
  }

  public async updateVersion(body: string[]) {
    const { data } = await http.put<boolean>(`${this.url}/update-version`, body);
    return data;
  }

  public async suspended(body: { device_ids: string[] }) {
    const { data } = await http.put<boolean>(`${this.url}/suspended`, body);
    return data;
  }

  public async unsuspended(body: { device_ids: string[] }) {
    const { data } = await http.put<boolean>(`${this.url}/unsuspended`, body);
    return data;
  }

  public async gps(body: string[]) {
    const { data } = await http.put<boolean>(`${this.url}/gps`, body);
    return data;
  }

  public async ban(id: string) {
    const { data } = await http.put<boolean>(`${this.url}/ban/${id}`);
    return data;
  }

  public async unban(id: string) {
    const { data } = await http.put<boolean>(`${this.url}/unban/${id}`);
    return data;
  }

  public async exportExcel(params?: IDeviceQuery) {
    const res = await http.get<string>(`${this.url}/export-excel`, { params });
    return res;
  }

  public async importExcel(file: File, body: IImportDeviceBody) {
    const formData = new FormData();
    body.province_id && formData.append('province_id', body.province_id || '');
    body.commune_id && formData.append('commune_id', body.commune_id || '');
    body.village_id && formData.append('village_id', body.village_id || '');

    if (file) {
      formData.append('file', file);
    }

    const { data } = await http.post(`${this.url}/import-excel`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      timeout: 1000 * 60 * 3,
    });
    return data;
  }

  public async location(id: string, body: ILocationDeviceBody) {
    const res = await http.put<boolean>(`${this.url}/location/${id}`, body);
    return res;
  }

  public async license(body: ILicenseDeviceBody) {
    const res = await http.post<string>(`${this.url}/license`, body);
    return res.data;
  }

  public async share(id: string, body: IShareDeviceBody) {
    const res = await http.post<IShare>(`${this.url}/share/${id}`, body);
    return res.data;
  }

  public async shareMulti(body: IShareDeviceMultiBody) {
    const res = await http.post<IShare>(`${this.url}/share-multi`, body);
    return res.data;
  }

  public async acceptShare(id: string) {
    const res = await http.put<boolean>(`${this.url}/share/${id}`);
    return res.data;
  }

  public async cancelShare(id: string) {
    const res = await http.delete<boolean>(`${this.url}/share/${id}`);
    return res.data;
  }

  public async listShare(id: string) {
    const res = await http.get<IShare[]>(`${this.url}/share/${id}`);
    return res.data;
  }
}
